import { loadGoogleMaps } from './loader.js';
import { createMapClickHandler, createLoadingElement, createErrorElement } from './ui.js';
import { resolveMapPoints, getOffsetCoordinate } from './utils.js';
import { getCardComponent } from '../blocks/cards/registry.js';
import { h, render } from 'preact';
import htm from 'htm';

const html = htm.bind(h);

export async function mountGoogleMap(container, payload, apiKey, { t, config }, cancellationState) {
  let activeInfoWindow = null;
  const closePopupFn = () => {
    if (activeInfoWindow) {
      activeInfoWindow.close();
      activeInfoWindow = null;
    }
  };
  const handleMapClick = createMapClickHandler(config);
  container.addEventListener('click', handleMapClick);

  const loadingEl = createLoadingElement(t);
  container.appendChild(loadingEl);

  let google;
  try {
    google = await loadGoogleMaps(apiKey);
  } catch (err) {
    if (loadingEl.parentNode === container) container.removeChild(loadingEl);
    const retryFn = () => {
      if (errorEl.parentNode === container) container.removeChild(errorEl);
      mountGoogleMap(container, payload, apiKey, { t, config }, cancellationState);
    };
    const errorEl = createErrorElement(t, retryFn);
    container.appendChild(errorEl);
    return {
      destroy() {
        container.removeEventListener('click', handleMapClick);
        if (errorEl.parentNode === container) container.removeChild(errorEl);
      },
      update() { }
    };
  }

  if (cancellationState.isDestroyed) {
    if (loadingEl.parentNode === container) container.removeChild(loadingEl);
    return {
      destroy() { container.removeEventListener('click', handleMapClick); },
      update() { }
    };
  }

  const gMaps = google;
  const checkConstructors = () => typeof gMaps?.maps === "object" &&
    typeof gMaps.maps.Map === "function" &&
    typeof gMaps.maps.LatLngBounds === "function" &&
    typeof gMaps.maps.Marker === "function";

  if (!checkConstructors()) {
    try {
      await new Promise((resolve, reject) => {
        let attempts = 0;
        const interval = setInterval(() => {
          if (cancellationState.isDestroyed) {
            clearInterval(interval);
            reject(new Error("Map mounting cancelled."));
            return;
          }
          attempts++;
          if (checkConstructors()) {
            clearInterval(interval);
            resolve();
          } else if (attempts > 100) {
            clearInterval(interval);
            reject(new Error("Google Maps constructors failed to initialize."));
          }
        }, 50);
      });
    } catch (err) {
      if (loadingEl.parentNode === container) container.removeChild(loadingEl);
      if (cancellationState.isDestroyed) return { destroy() {}, update() {} };
      
      const retryFn = () => {
        if (errorEl.parentNode === container) container.removeChild(errorEl);
        mountGoogleMap(container, payload, apiKey, { t, config }, cancellationState);
      };
      const errorEl = createErrorElement(t, retryFn);
      container.appendChild(errorEl);
      return {
        destroy() {
          container.removeEventListener('click', handleMapClick);
          if (errorEl.parentNode === container) container.removeChild(errorEl);
        },
        update() { }
      };
    }
  }

  if (loadingEl.parentNode === container) container.removeChild(loadingEl);
  if (cancellationState.isDestroyed) {
    return {
      destroy() { container.removeEventListener('click', handleMapClick); },
      update() { }
    };
  }

  const mapEl = document.createElement("div");
  mapEl.className = "mta-google-map";
  mapEl.style.width = "100%";
  mapEl.style.height = "100%";
  container.appendChild(mapEl);

  const accentColor = getComputedStyle(container).getPropertyValue("--mta-accent")?.trim() || "#0d6efd";

  const map = new gMaps.maps.Map(mapEl, {
    zoom: 2,
    center: { lat: 20, lng: 0 },
    mapTypeControl: false,
    fullscreenControl: false,
    streetViewControl: false,
    zoomControl: true
  });

  let markers = [];
  let polylineObj = null;

  function renderPointsGoogle(data) {
    markers.forEach(m => m.setMap(null));
    markers = [];
    if (polylineObj) {
      polylineObj.setMap(null);
      polylineObj = null;
    }
    if (activeInfoWindow) {
      activeInfoWindow.close();
      activeInfoWindow = null;
    }

    const resolved = resolveMapPoints(data);
    if (resolved.length === 0) {
      map.setCenter({ lat: 20, lng: 0 });
      map.setZoom(2);
      return;
    }

    const bounds = new gMaps.maps.LatLngBounds();
    const pathCoords = [];

    let singleMarker = null;
    let singleInfoWindow = null;

    resolved.forEach(pt => {
      const { lat, lng } = getOffsetCoordinate(pt._resolvedLat, pt._resolvedLng, pt._duplicateIndex);
      const order = pt.order ?? pt.day ?? (pt._resolvedIndex + 1);
      const name = pt.name ?? pt.label ?? `Point ${order}`;
      const CardComponent = getCardComponent(pt.type);
      const popupContainer = document.createElement('div');
      render(html`<${CardComponent} item=${pt} ctx=${{ t, config }} variant="popup" onClose=${closePopupFn} />`, popupContainer);

      const markerSvg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22">
        <circle cx="11" cy="11" r="9" fill="${accentColor}" stroke="#ffffff" stroke-width="2"/>
      </svg>
      `;

      const icon = {
        url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(markerSvg),
        size: new gMaps.maps.Size(22, 22),
        origin: new gMaps.maps.Point(0, 0),
        anchor: new gMaps.maps.Point(11, 11)
      };

      const marker = new gMaps.maps.Marker({
        position: { lat: lat, lng: lng },
        map: map,
        icon: icon,
        title: name
      });

      const infoWindow = new gMaps.maps.InfoWindow({
        content: popupContainer,
        minWidth: 280,
        maxWidth: 320
      });

      marker.addListener("click", () => {
        if (activeInfoWindow && activeInfoWindow !== infoWindow) {
          activeInfoWindow.close();
        }
        infoWindow.open(map, marker);
        activeInfoWindow = infoWindow;
      });

      markers.push(marker);
      const latLng = new gMaps.maps.LatLng(lat, lng);
      pathCoords.push(latLng);
      bounds.extend(latLng);

      if (resolved.length === 1) {
        singleMarker = marker;
        singleInfoWindow = infoWindow;
      }
    });

    if (data.polyline && pathCoords.length >= 2) {
      polylineObj = new gMaps.maps.Polyline({
        path: pathCoords,
        geodesic: true,
        strokeColor: accentColor,
        strokeOpacity: 0.8,
        strokeWeight: 3,
        map: map
      });
    }

    if (isFinite(data.center_latitude) && isFinite(data.center_longitude)) {
      map.setCenter({ lat: data.center_latitude, lng: data.center_longitude });
      map.setZoom(data.zoom || 13);
    } else if (pathCoords.length === 1) {
      map.setCenter(pathCoords[0]);
      map.setZoom(13);
    } else {
      map.fitBounds(bounds);
    }

    if (singleMarker && singleInfoWindow) {
      singleInfoWindow.open(map, singleMarker);
      activeInfoWindow = singleInfoWindow;
    }
  }

  renderPointsGoogle(payload);

  const resizeObserver = new ResizeObserver(() => {
    gMaps.maps.event.trigger(map, "resize");
  });
  resizeObserver.observe(container);

  return {
    destroy() {
      container.removeEventListener('click', handleMapClick);
      resizeObserver.disconnect();
      markers.forEach(m => m.setMap(null));
      if (polylineObj) polylineObj.setMap(null);
      if (mapEl.parentNode === container) container.removeChild(mapEl);
    },
    update(newData) {
      if (!cancellationState.isDestroyed) renderPointsGoogle(newData);
    },
    invalidate() {
      if (!cancellationState.isDestroyed) gMaps.maps.event.trigger(map, "resize");
    }
  };
}
